"use client";

import { Api } from "@/app/api";
import { AffiliateUser, OnboardingStages, UpdateUser, User, UserResponseDto } from "@/types";

export const userInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		getUsers: builder.query<UserResponseDto, void>({
			query: () => ({
				url: "/users?pagination=false",
			}),
		}),

		getAggregatedUsers: builder.query<UserResponseDto, void>({
			query: () => ({
				url: "/users?pagination=false&aggregated=true",
			}),
		}),

		getUser: builder.query<User, string>({
			query: (userId) => ({
				url: `/users/${userId}`,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["user"],
		}),

		getAffiliatedUsers: builder.query<UserResponseDto, string>({
			query: (userId) => ({
				url: `/users`,
				params: {
					affiliatedBy: userId,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
		}),
		getAggregatedUsersByUserId: builder.query<AffiliateUser[], string>({
			query: (userId) => ({
				url: `/users/${userId}/affiliate-view`,
			}),
		}),

		deleteUser: builder.mutation<{ message: string; filesDeleted: number }, string>({
			query: (userId) => ({
				url: `/users/${userId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["user", { type: "user", id: "support-account" }],
		}),

		updateUser: builder.mutation<User, { userId: string; patchUser: UpdateUser }>({
			query: ({ userId, patchUser }) => ({
				url: `/users/${userId}`,
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(patchUser),
				method: "PATCH",
			}),
			invalidatesTags: [
				"user",
				{ type: "user", id: "support-account" },
			],
		}),

		assignRole: builder.mutation<User, { userId: string; role: string }>({
			query: ({ userId, role }) => ({
				url: `/users/${userId}/role`,
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ role }),
				method: "PATCH",
			}),
			invalidatesTags: ["user", { type: "user", id: "support-account" }],
		}),

		searchUsers: builder.query<
			UserResponseDto,
			{
				searchQuery?: string;
				sortBy?: string;
				page?: number;
				limit?: number;
				pagination?: boolean;
				onboardingStage?: OnboardingStages;
				/** Admin directory: exclude or isolate legacy import placeholder emails */
				directorySegment?: "users" | "legacy";
			}
		>({
			query: ({ searchQuery, sortBy, page, limit, pagination, onboardingStage, directorySegment }) => ({
				url: `/users`,
				params: {
					page: page,
					limit: limit,
					pagination,
					search: searchQuery,
					sortBy: sortBy,
					onboardingStage,
					...(directorySegment ? { directorySegment } : {}),
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
		}),
	}),
});

export const {
	useGetUsersQuery,
	useGetUserQuery,
	useUpdateUserMutation,
	useAssignRoleMutation,
	useDeleteUserMutation,
	useSearchUsersQuery,
	useGetAggregatedUsersQuery,
	useGetAggregatedUsersByUserIdQuery,
} = userInjection;
